import React, { type SyntheticEvent, type ReactNode, useCallback, useRef } from 'react';
import { xcss, Pressable } from '@atlaskit/primitives';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { asyncCreateIssueTypeModalEntryPoint } from '@atlassian/jira-create-issue-type-modal/entrypoint.tsx';
import { ModalEntryPointPressableTrigger } from '@atlassian/jira-modal-entry-point-pressable-trigger/src/ModalEntryPointPressableTrigger.tsx';
import {
	type ConfigureSource,
	GIC_VIEW_NO_RESULTS_SOURCE,
} from '@atlassian/jira-issue-type-config-common/src/types.tsx';
import type { UFOExperience } from '@atlassian/ufo';
import { fg } from '@atlassian/jira-feature-gating';
import { defaultModalProps } from '@atlassian/jira-issue-type-config-common/src/constants.tsx';
// eslint-disable-next-line jira/deprecations/ban-identifiers
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { FormDialogSkeleton } from '@atlassian/jira-form-dialog-skeleton/src/ui/FormDialogSkeleton.tsx';
import { Trigger, useTriggerContext } from '@atlassian/entry-point-panel';
import { ConfigureWorkTypePanel } from '../ConfigureWorkTypePanel.tsx';

type CreateWorkTypeButtonProps = {
	children: ReactNode;
	onClick: () => void;
	onMouseEnter: () => void;
	entryPointProps: {
		onIssueTypeCreated: () => void;
		issueTypesCountLimit: number;
		onClose: () => void;
		onOpen: () => void;
		source: ConfigureSource;
		ufoExperience: UFOExperience;
		shouldFetchProjectData: boolean;
	};
	entryPointParams: {
		cloudId: string;
		projectKey: string;
	};
	shouldTriggerModal: boolean;
};

export const CreateWorkTypeButton = ({
	children,
	onClick,
	onMouseEnter,
	entryPointProps,
	entryPointParams,
	shouldTriggerModal,
}: CreateWorkTypeButtonProps) => {
	const popupTrigger = useTriggerContext(ConfigureWorkTypePanel);
	const createWorkTypeButtonRef = useRef<HTMLElement>(null);

	const handleClick = useCallback(
		(e: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			onClick();

			// Will be handled in onClick when fg is on
			if (!fg('jira_gic_inline_create_work_type_refactor')) {
				fireUIAnalytics(analyticsEvent, 'createIssueTypeButton', {
					calledFrom: GIC_VIEW_NO_RESULTS_SOURCE,
				});
			}
		},
		[onClick],
	);

	if (!fg('jira_gic_inline_create_work_type_refactor')) {
		return (
			<Pressable
				onClick={handleClick}
				onMouseEnter={onMouseEnter}
				xcss={createWorkTypeInlineNoResultStyles}
			>
				{children}
			</Pressable>
		);
	}

	// eslint-disable-next-line jira/ff/unsafe-no-exposure
	const [inlineIssueTypeConfigProductionizationExpConfig] = UNSAFE_noExposureExp(
		'inline_issue_type_config_productionization_exp',
	);

	const getClickElement = (ref: React.RefObject<HTMLElement>) => (
		<Pressable
			// onClick is intercepted by ModalEntryPointPressableTrigger/Trigger component via usePressablePreloadRef,
			// so we need to manually trigger a synthetic click on left mouse click
			onMouseDown={(e) => {
				// Left mouse button was clicked
				if (e.button === 0) {
					createWorkTypeButtonRef?.current?.click();
				}
			}}
			onKeyDown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					createWorkTypeButtonRef?.current?.click();
				}
			}}
			onClick={handleClick}
			xcss={createWorkTypeInlineNoResultStyles}
			// @ts-expect-error - The reference type from ModalEntryPointPressableTrigger is incompatible with Pressable,
			// but this works correctly at runtime and is a type-system limitation
			ref={ref}
		>
			{children}
		</Pressable>
	);

	return shouldTriggerModal ? (
		// TODO (JGFENG-1237) Migrate deprecated ModalEntryPointPressableTrigger
		<ModalEntryPointPressableTrigger
			entryPoint={asyncCreateIssueTypeModalEntryPoint}
			entryPointProps={entryPointProps}
			entryPointParams={entryPointParams}
			interactionName="create-issue-type-modal"
			modalProps={defaultModalProps}
			useInternalModal={inlineIssueTypeConfigProductionizationExpConfig.get('isEnabled', false)}
			Fallback={() => <FormDialogSkeleton numFormFields={1} />}
		>
			{({ ref }) => getClickElement(ref)}
		</ModalEntryPointPressableTrigger>
	) : (
		<Trigger
			trigger={popupTrigger}
			entryPoint={asyncCreateIssueTypeModalEntryPoint}
			entryPointProps={entryPointProps}
			entryPointParams={entryPointParams}
			fallback={<FormDialogSkeleton numFormFields={1} />}
			forwardedRef={createWorkTypeButtonRef}
		>
			{({ ref }) => getClickElement(ref)}
		</Trigger>
	);
};

const createWorkTypeInlineNoResultStyles = xcss({
	paddingBlockStart: 'space.0',
	paddingInline: 'space.0',
	marginTop: 'space.100',
	borderRadius: token('space.025'),
	color: 'color.link',
	backgroundColor: 'color.background.neutral.subtle',
	display: 'block',
	fontWeight: 'font.weight.bold',
});
